import { ExtendedHelpers, SelectorPair, TutorialStep } from '../types'
import { dependencies, stepPrerequisites } from '../dependencies'
import { openDependencyDirectly } from './directAccess'
import { isElementReady } from './elementUtils'
import { Tabs } from '../../layout/MainLayout'

type Contexts = {
  modalContext?: any
  showcaseContext?: any
  setActiveTab?: (tab: { tab: Tabs; options?: any }) => void
}

const closeDependency = async (dep: SelectorPair, contexts: Contexts): Promise<void> => {
  if (!isElementReady(dep.selector)) {
    return
  }

  if (dep === stepPrerequisites.rerunDialog && contexts.modalContext?.setIsBacktestRunDialogOpen) {
    contexts.modalContext.setIsBacktestRunDialogOpen(false)
    return
  }
  if (dep === stepPrerequisites.backtestSideNav && contexts.showcaseContext?.setFocusedBacktest) {
    contexts.showcaseContext.setFocusedBacktest(null)
    return
  }
  if (dep === stepPrerequisites.alertSideNav) {
    const alertSidePanel = document.querySelector(dep.selector)
    if (alertSidePanel) {
      const closeButtons = alertSidePanel.querySelectorAll('button')
      for (const button of Array.from(closeButtons)) {
        const hasCloseIcon = button.querySelector('svg[data-testid="CloseIcon"]')
        if (hasCloseIcon) {
          ;(button as HTMLElement).click()
          return
        }
      }
    }
    return
  }
  if (dep === stepPrerequisites.backtestTypeSelectMenu && window.closeTourTypeSelectMenu) {
    window.closeTourTypeSelectMenu()
    return
  }

  const depElement = document.querySelector(dep.selector)
  if (!depElement) return

  const closeBtn = depElement.querySelector(
    '[aria-label*="close"], [aria-label*="Close"], button:has([data-testid="CloseIcon"])'
  ) as HTMLElement
  if (closeBtn) {
    closeBtn.click()
    return
  }

  const toggleButton = document.querySelector(dep.button) as HTMLElement
  if (toggleButton && isElementReady(dep.button)) {
    if (!depElement.contains(toggleButton)) {
      toggleButton.click()
      return
    }
  }

  depElement.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true })
  )
}

export const openRequiredDependenciesForStep = async (
  toStepIndex: number,
  allSteps: readonly TutorialStep[]
): Promise<void> => {
  const toStep = allSteps[toStepIndex]
  if (!toStep) return

  const toTarget =
    typeof toStep.target === 'string'
      ? toStep.target
      : (toStep.target as SelectorPair).selector || ''
  const requiredDependencies = dependencies[toTarget] || []

  for (const dep of requiredDependencies) {
    if (!isElementReady(dep.selector)) {
      try {
        await openDependencyDirectly(dep as SelectorPair)
        await new Promise((resolve) => setTimeout(resolve, 300))
      } catch (error) {
        console.error(`[Tour] Failed to open dependency for step ${toStepIndex}:`, dep, error)
      }
    }
  }
}

export const cleanupDependenciesForStepTransition = async (
  fromStepIndex: number,
  toStepIndex: number,
  allSteps: readonly TutorialStep[],
  modalContext?: any,
  showcaseContext?: any,
  setActiveTab?: (tab: { tab: Tabs; options?: any }) => void
): Promise<void> => {
  await openRequiredDependenciesForStep(toStepIndex, allSteps)

  const fromStep = allSteps[fromStepIndex]
  const toStep = allSteps[toStepIndex]
  if (!fromStep || !toStep) return

  const fromTarget =
    typeof fromStep.target === 'string'
      ? fromStep.target
      : (fromStep.target as SelectorPair).selector || ''
  const toTarget =
    typeof toStep.target === 'string'
      ? toStep.target
      : (toStep.target as SelectorPair).selector || ''

  const fromDependencies = dependencies[fromTarget] || []
  const toDependencySet = new Set(dependencies[toTarget] || [])

  const dependenciesToClose = fromDependencies.filter((dep) => !toDependencySet.has(dep))

  for (const dep of dependenciesToClose.reverse()) {
    await closeDependency(dep as SelectorPair, { modalContext, showcaseContext, setActiveTab })
    await new Promise((resolve) => setTimeout(resolve, 150))
  }
}

export const cleanupUnnecessaryDependencies = async (
  targetSectionSteps: readonly TutorialStep[],
  currentSteps: readonly TutorialStep[],
  currentStepIndex: number,
  helpers: ExtendedHelpers,
  modalContext?: any,
  showcaseContext?: any,
  isJumpNavigation?: boolean
): Promise<void> => {
  const targetDependencies = new Set<string>()
  targetSectionSteps.forEach((step) => {
    const stepTarget =
      typeof step.target === 'string' ? step.target : (step.target as SelectorPair).selector || ''
    const deps = dependencies[stepTarget]
    if (deps) {
      deps.forEach((dep) => targetDependencies.add(`${dep.button}|${dep.selector}`))
    }
  })

  const currentStep = currentSteps[currentStepIndex]
  if (!currentStep) return

  const currentSectionSteps = (currentSteps as TutorialStep[]).filter(
    (step) => step.section === currentStep.section
  )

  const currentSectionDependencies = new Map<string, SelectorPair>()
  currentSectionSteps.forEach((step) => {
    const stepTarget =
      typeof step.target === 'string' ? step.target : (step.target as SelectorPair).selector || ''
    const stepDeps = dependencies[stepTarget] || []
    stepDeps.forEach((dep) => {
      const key = `${dep.button}|${dep.selector}`
      if (!currentSectionDependencies.has(key)) {
        currentSectionDependencies.set(key, dep as SelectorPair)
      }
    })
  })

  const dependenciesToClose = Array.from(currentSectionDependencies.values()).filter(
    (currentDep) => {
      const depKey = `${currentDep.button}|${currentDep.selector}`
      return !targetDependencies.has(depKey)
    }
  )

  if (dependenciesToClose.length > 0) {
    const shouldPauseTour = !isJumpNavigation
    if (shouldPauseTour && helpers?.close) helpers.close()

    for (const dep of dependenciesToClose) {
      await closeDependency(dep, { modalContext, showcaseContext })
    }

    await new Promise((resolve) => setTimeout(resolve, isJumpNavigation ? 200 : 800))

    if (shouldPauseTour && helpers) {
      helpers.update?.()
      setTimeout(() => helpers.open?.(), 300)
    } else if (isJumpNavigation) {
      helpers.update?.()
    }
  }
}

export const cleanupAllTourElements = async (contexts: Contexts): Promise<void> => {
  const allPossibleDependencies = Object.values(stepPrerequisites)

  for (const dep of allPossibleDependencies) {
    try {
      await closeDependency(dep, contexts)
      await new Promise((resolve) => setTimeout(resolve, 150))
    } catch (error) {
      console.error(`[Tour] Error closing dependency during full cleanup: ${dep.selector}`, error)
    }
  }

  await new Promise((resolve) => setTimeout(resolve, 200))
  const backdrop = document.querySelector('.MuiBackdrop-root') as HTMLElement
  if (backdrop && backdrop.offsetParent !== null) {
    backdrop.click()
  }
}
